import { useEffect, useRef } from 'react';
import { Star, Clock, Sparkles } from 'lucide-react';
import { TOOLS } from '@/registry/tools';
import { usePrefs } from '@/store/prefs';
import { CATEGORY_LABEL, type ToolDefinition } from '@/types/tool';
import type { BrowserSection, BrowserItem } from '@/hooks/useToolNavigation';
import { cn } from '@/lib/cn';

interface Props {
  query: string;
  visualItems: BrowserItem[];
  highlightIndex: number;
  setHighlightIndex: (i: number) => void;
  isHighlighted: (section: BrowserSection, toolId: string) => boolean;
  onPick: (id: string) => void;
}

const TOOLS_BY_ID = Object.fromEntries(TOOLS.map((t) => [t.id, t]));

export function ToolBrowser({
  query,
  visualItems,
  highlightIndex,
  setHighlightIndex,
  isHighlighted,
  onPick,
}: Props) {
  const favorites = usePrefs((s) => s.favorites);
  const recents = usePrefs((s) => s.recents);
  const isSearching = query.trim().length > 0;

  // Reset the highlight to the first row whenever the visible list changes.
  useEffect(() => {
    setHighlightIndex(0);
  }, [query, setHighlightIndex]);

  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-item-idx="${highlightIndex}"]`,
    );
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlightIndex]);

  // Split the flat visualItems list back into sections for rendering.
  // visualItems is already in section order (recent → favorites → all, or
  // just `results` when searching), so we just walk it once.
  const sections: Record<BrowserSection, Array<{ idx: number; tool: ToolDefinition }>> = {
    results: [],
    recent: [],
    favorites: [],
    all: [],
  };
  visualItems.forEach((item, idx) => {
    const tool = TOOLS_BY_ID[item.toolId];
    if (!tool) return;
    sections[item.section].push({ idx, tool });
  });

  if (isSearching && sections.results.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-center text-xs text-muted-foreground">
        No tools match “{query}”.
      </div>
    );
  }

  return (
    <div ref={listRef} className="flex flex-col gap-4 p-3">
      {isSearching ? (
        <Section title="Results" icon={Sparkles}>
          {sections.results.map(({ idx, tool }) => (
            <ToolItem
              key={`results-${tool.id}`}
              tool={tool}
              active={isHighlighted('results', tool.id)}
              onClick={() => onPick(tool.id)}
              onMouseEnter={() => setHighlightIndex(idx)}
              dataIdx={idx}
            />
          ))}
        </Section>
      ) : (
        <>
          {recents.length > 0 && sections.recent.length > 0 ? (
            <Section title="Recent" icon={Clock}>
              {sections.recent.map(({ idx, tool }) => (
                <ToolItem
                  key={`recent-${tool.id}`}
                  tool={tool}
                  active={isHighlighted('recent', tool.id)}
                  onClick={() => onPick(tool.id)}
                  onMouseEnter={() => setHighlightIndex(idx)}
                  dataIdx={idx}
                />
              ))}
            </Section>
          ) : null}

          {favorites.length > 0 && sections.favorites.length > 0 ? (
            <Section title="Favorites" icon={Star}>
              {sections.favorites.map(({ idx, tool }) => (
                <ToolItem
                  key={`favorites-${tool.id}`}
                  tool={tool}
                  active={isHighlighted('favorites', tool.id)}
                  onClick={() => onPick(tool.id)}
                  onMouseEnter={() => setHighlightIndex(idx)}
                  dataIdx={idx}
                />
              ))}
            </Section>
          ) : null}

          <Section title="All tools">
            {sections.all.map(({ idx, tool }) => (
              <ToolItem
                key={`all-${tool.id}`}
                tool={tool}
                active={isHighlighted('all', tool.id)}
                category
                onClick={() => onPick(tool.id)}
                onMouseEnter={() => setHighlightIndex(idx)}
                dataIdx={idx}
              />
            ))}
          </Section>
        </>
      )}
    </div>
  );
}

function Section({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon?: typeof Star;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h2 className="mb-1.5 flex items-center gap-1.5 px-1 text-2xs font-medium uppercase tracking-wider text-muted-foreground">
        {Icon ? <Icon className="h-3 w-3" /> : null} {title}
      </h2>
      <div className="flex flex-col gap-0.5">{children}</div>
    </section>
  );
}

function ToolItem({
  tool,
  active,
  category,
  onClick,
  onMouseEnter,
  dataIdx,
}: {
  tool: ToolDefinition;
  active: boolean;
  category?: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  dataIdx: number;
}) {
  const Icon = tool.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      data-item-idx={dataIdx}
      className={cn(
        'group flex w-full items-center gap-2.5 rounded-md border border-transparent px-2 py-1.5 text-left transition-colors',
        'hover:bg-accent',
        active && 'border-border bg-accent',
      )}
    >
      <div
        className={cn(
          'flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-secondary text-secondary-foreground',
          active && 'bg-primary/15 text-primary',
        )}
      >
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-xs font-medium">{tool.name}</div>
        <div className="truncate text-2xs text-muted-foreground">
          {tool.description}
        </div>
      </div>
      {category ? (
        <span className="hidden text-2xs text-muted-foreground sm:inline">
          {CATEGORY_LABEL[tool.category]}
        </span>
      ) : null}
    </button>
  );
}
